import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { formatMoney, formatUsd } from '../../project-modules/store/projectCostEstimate';
import { usePublicUiLocale } from '../../contexts/PublicUiLocaleContext';
import HelpHint from '../../components/HelpHint';

function LineRow({ label, detail, amount, emphasize, recurring }) {
  const formatted = recurring ? formatUsd(amount) : formatMoney(amount);
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="baseline">
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={emphasize ? 600 : 400}>
          {label}
        </Typography>
        {detail ? (
          <Typography variant="caption" color="text.secondary" display="block">
            {detail}
          </Typography>
        ) : null}
      </Box>
      <Typography variant="body2" fontWeight={emphasize ? 700 : 500} sx={{ flexShrink: 0 }}>
        {formatted}
      </Typography>
    </Stack>
  );
}

LineRow.propTypes = {
  label: PropTypes.string.isRequired,
  detail: PropTypes.string,
  amount: PropTypes.number.isRequired,
  emphasize: PropTypes.bool,
  recurring: PropTypes.bool,
};

LineRow.defaultProps = {
  detail: '',
  emphasize: false,
  recurring: false,
};

export default function PurchaseInvoiceDialog({
  open,
  title,
  subtitle,
  quote,
  locale,
  submitting,
  onClose,
  onConfirm,
}) {
  const { sa } = usePublicUiLocale();

  if (!quote) return null;

  const canAfford = quote.can_afford !== false;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {title}
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
            {subtitle}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {locale.invoiceIntro}
          </Typography>
          <HelpHint title={sa.helpInvoiceTitle} text={sa.helpInvoice} />
        </Box>

        {quote.charge_now_lines?.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="overline" color="text.secondary">
              {locale.chargeNowSection}
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {quote.charge_now_lines.map((line) => (
                <LineRow
                  key={line.id}
                  label={line.label}
                  detail={line.detail}
                  amount={line.amount}
                  recurring
                />
              ))}
            </Stack>
          </Box>
        ) : null}

        <Typography variant="overline" color="text.secondary">
          {locale.monthlySection}
        </Typography>
        <Stack spacing={1} sx={{ mt: 1, mb: 2 }}>
          {(quote.lines || []).map((line) => (
            <LineRow
              key={line.id}
              label={line.label}
              detail={line.detail}
              amount={line.amount}
              recurring
            />
          ))}
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        <LineRow
          label={locale.chargeNowTotal}
          amount={quote.charge_now_usd}
          emphasize
          recurring
        />
        <Box sx={{ mt: 1 }}>
          <LineRow
            label={locale.monthlyRecurring}
            detail={locale.monthlyRecurringHint}
            amount={quote.monthly_recurring_usd}
            recurring
          />
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Stack spacing={0.5}>
          <LineRow label={locale.balanceAfter} amount={quote.balance_usd - quote.charge_now_usd} />
          <Typography variant="caption" color="text.secondary">
            {locale.balanceCurrent.replace('{amount}', formatMoney(quote.balance_usd))}
            {' · '}
            {locale.availableCredit.replace('{amount}', formatMoney(quote.available_credit_usd))}
          </Typography>
        </Stack>

        {!canAfford ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {locale.insufficientBalance}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          {locale.cancel}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={submitting || !canAfford}
        >
          {locale.confirmPay}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

PurchaseInvoiceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  quote: PropTypes.object,
  locale: PropTypes.object.isRequired,
  submitting: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

PurchaseInvoiceDialog.defaultProps = {
  subtitle: '',
  quote: null,
  submitting: false,
};
